import { useCallback, useEffect, useState } from "react";
import { metenApi } from "../services/metenApi";

export const MONITORING_REFRESH_MS = 5000;

export default function useAdminUserMonitoring() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision(value => value + 1), []);

  useEffect(() => {
    let stopped = false;
    let busy = false;
    let denied = false;
    let timer;
    let requestTimeout;
    let controller;

    async function poll() {
      clearTimeout(timer);
      if (stopped || busy || denied || document.hidden) return;
      if (navigator.onLine === false) {
        setError(new Error("offline"));
        return;
      }
      busy = true;
      setLoading(true);
      controller = new AbortController();
      requestTimeout = setTimeout(() => controller.abort(), 15000);
      try {
        const snapshot = await metenApi.getUserMonitoring({ signal: controller.signal });
        if (stopped) return;
        if (!snapshot?.counts || !Array.isArray(snapshot.users) || !Array.isArray(snapshot.active_jobs)) {
          throw new Error("Invalid monitoring response");
        }
        setData(snapshot);
        setError(null);
      } catch (err) {
        if (stopped) return;
        setError(err);
        if (err.status === 401 || err.status === 403) {
          denied = true;
          setData(null);
        }
      } finally {
        clearTimeout(requestTimeout);
        busy = false;
        if (!stopped) {
          setLoading(false);
          if (!denied && !document.hidden) timer = setTimeout(poll, MONITORING_REFRESH_MS);
        }
      }
    }

    function visibilityChanged() {
      clearTimeout(timer);
      if (!document.hidden) poll();
    }
    function offline() {
      clearTimeout(timer);
      setError(new Error("offline"));
    }
    poll();
    document.addEventListener("visibilitychange", visibilityChanged);
    window.addEventListener("online", poll);
    window.addEventListener("offline", offline);
    return () => {
      stopped = true;
      clearTimeout(timer);
      clearTimeout(requestTimeout);
      controller?.abort();
      document.removeEventListener("visibilitychange", visibilityChanged);
      window.removeEventListener("online", poll);
      window.removeEventListener("offline", offline);
    };
  }, [revision]);

  return { data, error, loading, refresh };
}
